'use client'

import { CustomToolbar } from '../CustomToolbar/CustomToolbar'
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  gridColumnDefinitionsSelector,
  GridColumnMenu,
  GridColumnMenuProps,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridPreferencePanelsValue,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import { DateDataGridFilter, TextFieldDataGridFilter } from '@repo/clients-matters-tables/DataGridTableCustomFilters'
import { formatDate } from '@repo/utils/time'
import { MoreMenuCard } from '../MoreMenuCard/MoreMenuCard'
import { Pill } from '@repo/ui/Pill'
import { TableClearFilters } from '@repo/ui/TableClearFilters'
import { ThirdPartyFormsCreatedByFilter } from '../DataGridFilters/ThirdPartyFormsCreatedByFilter'
import { ThirdPartyFormsStatusFilter } from '../DataGridFilters/ThirdPartyFormsStatusFilter'
import {
  THIRD_PARTY_FORMS_STATUS_BY_VALUE,
  THIRD_PARTY_FORMS_STATUS_FALLBACK
} from '../DataGridFilters/thirdPartyFormsStatuses'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useThirdPartyFormsStore } from '@repo/store/useThirdPartyFormsStore'
import { useUserPreferencesStore } from '@repo/store/useUserPreferencesStore'
import { useWindowSize } from '@uidotdev/usehooks'
import defaultThirdPartyFormsTableConfig from '@repo/store/defaultThirdPartyFormsTableConfig'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

const PREFERENCE_CATEGORY = 'Third Party Forms'
const PREFERENCE_KEY = 'MatterDocument_GetAllExtendedBySearchCriteria'

const parseMaybeJson = (val: unknown) => {
  if (!val) return {}
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch {
      return {}
    }
  }
  if (typeof val === 'object') return val as Record<string, any>
  return {}
}

const OPERATOR_KEYS = new Set(['dateComparisonOperator', 'numberComparisonOperator', 'operator'])

const isEmptyPrimitive = (x: unknown) => x === null || x === undefined || (typeof x === 'string' && x.trim() === '')

const isFilterValueActive = (v: any): boolean => {
  if (isEmptyPrimitive(v)) return false
  if (Array.isArray(v)) return v.some((item) => !isEmptyPrimitive(item))
  switch (typeof v) {
    case 'boolean':
    case 'number':
      return true
    case 'string':
      return v.trim() !== ''
    case 'object': {
      if ('value' in v) return !isEmptyPrimitive((v as any).value)
      if ('date' in v) return !isEmptyPrimitive((v as any).date)
      return Object.entries(v as Record<string, any>).some(
        ([k, val]) => !OPERATOR_KEYS.has(k) && isFilterValueActive(val)
      )
    }
    default:
      return false
  }
}

const areFiltersEffectivelyEmpty = (f: Record<string, any> | null | undefined) => {
  if (!f) return true
  return !Object.values(f).some((v) => isFilterValueActive(v))
}

const emptyOutFilters = (f: Record<string, any> | null | undefined) => {
  if (!f) return {}
  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(f)) {
    if (v && typeof v === 'object') {
      if ('date' in v || 'dateComparisonOperator' in v) {
        out[k] = { date: null, dateComparisonOperator: null }
      } else if ('value' in v || 'numberComparisonOperator' in v) {
        out[k] = { value: null, numberComparisonOperator: null }
      } else {
        out[k] = null
      }
    } else {
      out[k] = null
    }
  }
  return out
}

const renderTruncatedCell = (params: GridRenderCellParams) => {
  const value = params.value ?? ''
  return (
    <MUITooltip placement="bottom" arrow title={value}>
      <MUITypography variant="body1" noWrap>
        {value || '-'}
      </MUITypography>
    </MUITooltip>
  )
}

const renderPlainCell = (params: GridRenderCellParams) => (
  <MUITypography variant="body1" noWrap>
    {params.value || '-'}
  </MUITypography>
)

const renderFormNameCell = (params: GridRenderCellParams) => (
  <MUITypography variant="body1" sx={{ fontWeight: 600 }} noWrap>
    {params.value || '-'}
  </MUITypography>
)

const renderDateCell = (params: GridRenderCellParams) => {
  if (!params.value) return <MUITypography variant="body1">-</MUITypography>
  return <MUITypography variant="body1">{formatDate(params.value, 'd MMM yyyy')}</MUITypography>
}

const renderStatusCell = (params: GridRenderCellParams) => {
  const raw = (params.value ?? '').toString()
  if (!raw) return null
  const item = THIRD_PARTY_FORMS_STATUS_BY_VALUE[raw.toLowerCase()]
  const bg = item?.backgroundColor ?? THIRD_PARTY_FORMS_STATUS_FALLBACK.backgroundColor
  const fg = item?.color ?? THIRD_PARTY_FORMS_STATUS_FALLBACK.color
  return <Pill label={raw.toUpperCase()} backgroundColor={bg} color={fg} sx={{ width: 'auto' }} />
}

const buildCreatedByCellRenderer =
  (lookupEmail: (userId: string) => string | undefined) => (params: GridRenderCellParams) => {
    const userId = params.value as string | null | undefined
    if (!userId) return <MUITypography variant="body1">-</MUITypography>
    const email = lookupEmail(userId)
    return (
      <MUIStack direction="column" sx={{ lineHeight: 1.2, py: 0.5 }}>
        <MUITypography variant="body1" sx={{ fontWeight: 600 }}>
          {userId}
        </MUITypography>
        {email && (
          <MUITypography variant="body1" sx={{ color: 'text.secondary' }}>
            {email}
          </MUITypography>
        )}
      </MUIStack>
    )
  }

const MORE_MENU_WIDTH = 56

const COLUMN_FLEX: Record<string, number> = {
  formName: 1,
  formDescription: 2,
  formProviderName: 1,
  matterHistoryContentId: 1,
  updatedDate: 0.8,
  createdBy: 1.2
}

const COLUMN_MIN_WIDTHS: Record<string, number> = {
  formName: 140,
  formDescription: 180,
  formProviderName: 120,
  matterHistoryContentId: 140,
  updatedDate: 120,
  createdBy: 180
}

export const ThirdPartyFormsTableModule = () => {
  const apiRef = useGridApiRef()
  const { currentMatter } = useMattersStore()
  const {
    matterDocumentsTableData,
    matterDocumentsTableLoading,
    getAllExtendedMatterDocumentsBySearchCriteria,
    resetMatterDocumentsTable
  } = useThirdPartyFormsStore()
  const { allUsers, fetchAllUsers } = useLookupsStore()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { height: windowHeight } = useWindowSize()

  const {
    thirdPartyFormsTableFilters,
    thirdPartyFormsTableFilterCount,
    setThirdPartyFormsTableFilters,
    setThirdPartyFormsTableFiltersFromPreferences,
    resetThirdPartyFormsTableFilters
  } = useDataGridFilterStore()

  const thirdPartyFormsPreferences = useUserPreferencesStore((state: any) => state?.[PREFERENCE_CATEGORY])
  const updateUserPreference = useUserPreferencesStore((state: any) => state.updateUserPreference)

  const activePref = useMemo(() => thirdPartyFormsPreferences?.[PREFERENCE_KEY], [thirdPartyFormsPreferences])

  const resolvedConfig = useMemo(() => {
    const rv = activePref?.resolvedValue ?? activePref?.userValue ?? activePref?.defaultValue ?? {}
    return parseMaybeJson(rv)
  }, [activePref])

  const activePrefId = activePref?.userConfigurationTypeId ?? null
  const fallback = defaultThirdPartyFormsTableConfig as Record<string, any>

  const [filtersReady, setFiltersReady] = useState(false)
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({})
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>({ left: [], right: [] })
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const [tableHeight, setTableHeight] = useState(windowHeight ? windowHeight - 126 : 0)

  const prefsHydratedRef = useRef(false)
  const firstSyncDoneRef = useRef(false)

  useEffect(() => {
    const offset = pageTitleBlockIsVisible ? 310 : 160
    if (windowHeight) setTableHeight(windowHeight - offset)
  }, [windowHeight, pageTitleBlockIsVisible])

  useEffect(() => {
    fetchAllUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      resetMatterDocumentsTable()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Hydrate column state from preferences (or fallback to defaults)
  useEffect(() => {
    setColumnOrder(resolvedConfig.columnOrder ?? fallback.columnOrder ?? [])
    setColumnVisibilityModel(resolvedConfig.columnVisibilityModel ?? fallback.columnVisibilityModel ?? {})
    setColumnWidths(resolvedConfig.columnWidths ?? fallback.columnWidths ?? {})
    setPinnedColumns({
      left: resolvedConfig.columnsPinnedLeft ?? fallback.columnsPinnedLeft ?? [],
      right: resolvedConfig.columnsPinnedRight ?? fallback.columnsPinnedRight ?? []
    })
    setSortModel(resolvedConfig.columnSort ?? fallback.columnSort ?? [{ field: 'updatedDate', sort: 'desc' as const }])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedConfig])

  // Hydrate filters from preferences (only on first load)
  useEffect(() => {
    const prefBag = resolvedConfig?.filters ?? null
    const storeEmpty = areFiltersEffectivelyEmpty(thirdPartyFormsTableFilters?.filters)
    const prefsEmpty = areFiltersEffectivelyEmpty(prefBag)

    if (prefsHydratedRef.current) {
      setFiltersReady(true)
      return
    }

    if (!storeEmpty) {
      prefsHydratedRef.current = true
      setFiltersReady(true)
      return
    }

    if (!prefsEmpty) {
      setThirdPartyFormsTableFiltersFromPreferences({ filters: prefBag ?? null })
      prefsHydratedRef.current = true
      setFiltersReady(true)
      return
    }

    setFiltersReady(true)
  }, [resolvedConfig?.filters, thirdPartyFormsTableFilters?.filters, setThirdPartyFormsTableFiltersFromPreferences])

  const emailByUserId = useMemo(() => {
    const map: Record<string, string> = {}
    ;(allUsers ?? []).forEach((u: any) => {
      if (u?.userId != null) map[u.userId] = u.email ?? u.emailAddress ?? ''
    })
    return map
  }, [allUsers])

  const lookupEmail = useMemo(() => (userId: string) => emailByUserId[userId], [emailByUserId])

  const clientNo = currentMatter?.clientNo ?? null
  const matterNo = currentMatter?.matterNo ?? null

  const useDeepCompareMemoize = (value: any) => {
    const ref = useRef(value)
    const signal = JSON.stringify(value)
    const prevSignal = JSON.stringify(ref.current)
    if (signal !== prevSignal) {
      ref.current = value
    }
    return ref.current
  }

  const deepFilters = useDeepCompareMemoize(thirdPartyFormsTableFilters)

  const updateTableConfigPreference = useCallback(
    (fieldsToChange: any) => {
      if (!activePrefId) return

      updateUserPreference({
        userConfigurationTypeId: activePrefId,
        value: {
          ...resolvedConfig,
          columnOrder,
          columnVisibilityModel,
          columnWidths,
          columnsPinnedLeft: pinnedColumns.left,
          columnsPinnedRight: pinnedColumns.right,
          columnSort: sortModel,
          ...fieldsToChange
        },
        convertJsonToString: true,
        categoryName: PREFERENCE_CATEGORY
      })
    },
    [
      activePrefId,
      resolvedConfig,
      updateUserPreference,
      sortModel,
      columnOrder,
      columnVisibilityModel,
      columnWidths,
      pinnedColumns
    ]
  )

  const runFetch = useCallback(() => {
    if (!filtersReady) return
    if (!clientNo || matterNo == null) return

    const filters = deepFilters?.filters || {}

    const computedOrderBy = sortModel
      .map((sortItem) => ({
        columnName: sortItem.field,
        direction: sortItem.sort === 'asc' ? 0 : 1
      }))
      .filter(Boolean)

    const orderByColumns = computedOrderBy.length > 0 ? computedOrderBy : null

    const deepFiltersString = JSON.stringify(deepFilters.filters)
    const currentConfigString = JSON.stringify(resolvedConfig.filters)
    const filtersHaveChanged = deepFiltersString !== currentConfigString

    if (firstSyncDoneRef.current && filtersHaveChanged) {
      updateTableConfigPreference({ filters: deepFilters.filters })
    }

    getAllExtendedMatterDocumentsBySearchCriteria({
      startAtIndex: deepFilters?.startAtIndex ?? 0,
      pageSize: deepFilters?.pageSize ?? 25,
      clientNo,
      matterNo,
      formName: filters.formName || null,
      formDescription: filters.formDescription || null,
      createdBy: filters.createdBy || null,
      date: filters.updatedDate?.date || null,
      dateComparisonOperator: filters.updatedDate?.dateComparisonOperator ?? 0,
      statuses: Array.isArray(filters.status) && filters.status.length > 0 ? filters.status : null,
      orderByColumns
    })

    if (!firstSyncDoneRef.current) firstSyncDoneRef.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady, deepFilters.filters, deepFilters.pageSize, deepFilters.startAtIndex, sortModel, clientNo, matterNo])

  // Fetch when filters, sort, or pagination changes
  useEffect(() => {
    runFetch()
  }, [runFetch])

  const handleRefresh = useCallback(() => {
    runFetch()
  }, [runFetch])

  const handleColumnsClick = useCallback(() => {
    apiRef.current?.showPreferences(GridPreferencePanelsValue.columns)
  }, [apiRef])

  const baseColumns = useMemo<Record<string, GridColDef>>(
    () => ({
      formName: {
        field: 'formName',
        headerName: 'Form name',
        sortable: true,
        filterable: true,
        renderCell: renderFormNameCell,
        renderHeaderFilter: () => <TextFieldDataGridFilter filterType="formName" isThirdPartyFormsTable />
      },
      formDescription: {
        field: 'formDescription',
        headerName: 'Form description',
        sortable: true,
        filterable: true,
        renderCell: renderTruncatedCell,
        renderHeaderFilter: () => <TextFieldDataGridFilter filterType="formDescription" isThirdPartyFormsTable />
      },
      formProviderName: {
        field: 'formProviderName',
        headerName: 'Provider',
        sortable: true,
        filterable: false,
        renderCell: renderPlainCell
      },
      matterHistoryContentId: {
        field: 'matterHistoryContentId',
        headerName: 'Matter History ref',
        sortable: true,
        filterable: false,
        renderCell: renderPlainCell
      },
      updatedDate: {
        field: 'updatedDate',
        headerName: 'Last updated',
        sortable: true,
        filterable: true,
        renderCell: renderDateCell,
        renderHeaderFilter: () => <DateDataGridFilter filterType="updatedDate" isThirdPartyFormsTable />
      },
      createdBy: {
        field: 'createdBy',
        headerName: 'Created by',
        sortable: true,
        filterable: true,
        renderCell: buildCreatedByCellRenderer(lookupEmail),
        renderHeaderFilter: () => <ThirdPartyFormsCreatedByFilter />
      },
      status: {
        field: 'status',
        headerName: 'Status',
        sortable: true,
        filterable: true,
        renderCell: renderStatusCell,
        renderHeaderFilter: () => <ThirdPartyFormsStatusFilter />
      },
      moreMenu: {
        field: 'moreMenu',
        headerName: '',
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        disableReorder: true,
        renderCell: (params: GridRenderCellParams) => <MoreMenuCard rowData={params.row} />
      }
    }),
    [lookupEmail]
  )

  const orderedColumns = useMemo<GridColDef[]>(() => {
    const order = columnOrder.length > 0 ? columnOrder : Object.keys(baseColumns)
    return order
      .map((field) => baseColumns[field])
      .filter(Boolean)
      .map((col) => {
        if (col.field === 'moreMenu') return { ...col, width: MORE_MENU_WIDTH }
        const resizedWidth = columnWidths[col.field]
        if (resizedWidth != null) {
          return { ...col, width: resizedWidth, minWidth: COLUMN_MIN_WIDTHS[col.field] }
        }
        return {
          ...col,
          flex: COLUMN_FLEX[col.field] ?? 1,
          minWidth: COLUMN_MIN_WIDTHS[col.field]
        }
      })
  }, [columnOrder, columnWidths, baseColumns])

  const onPinnedColumnsChange = useCallback(
    (updatedPinnedColumns: GridPinnedColumnFields) => {
      setPinnedColumns(updatedPinnedColumns)
      updateTableConfigPreference({
        columnsPinnedLeft: updatedPinnedColumns.left,
        columnsPinnedRight: updatedPinnedColumns.right
      })
    },
    [updateTableConfigPreference]
  )

  const onSortModelChange = (model: GridSortModel) => {
    setSortModel(model)
    updateTableConfigPreference({ columnSort: model })
  }

  const onColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(model)
    updateTableConfigPreference({ columnVisibilityModel: model })
  }

  const onColumnOrderChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const newColumnOrder = columnDefinitions.map((column: any) => column.field)
    setColumnOrder(newColumnOrder)
    updateTableConfigPreference({ columnOrder: newColumnOrder })
  }

  const onColumnWidthChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const newColumnWidths: Record<string, number> = {}
    columnDefinitions.forEach((column: any) => {
      newColumnWidths[column.field] = column.width
    })
    setColumnWidths(newColumnWidths)
    updateTableConfigPreference({ columnWidths: newColumnWidths })
  }

  const handleClearAll = useCallback(() => {
    resetThirdPartyFormsTableFilters()
    const emptied = emptyOutFilters(resolvedConfig?.filters ?? thirdPartyFormsTableFilters?.filters ?? {})
    updateTableConfigPreference({ filters: emptied })
  }, [
    resetThirdPartyFormsTableFilters,
    resolvedConfig?.filters,
    thirdPartyFormsTableFilters?.filters,
    updateTableConfigPreference
  ])

  const paginationModel: GridPaginationModel = {
    page: Math.floor((thirdPartyFormsTableFilters?.startAtIndex ?? 0) / (thirdPartyFormsTableFilters?.pageSize ?? 25)),
    pageSize: thirdPartyFormsTableFilters?.pageSize ?? 25
  }

  const getTogglableColumns = (cols: GridColDef[]) =>
    cols.filter((column) => column.hideable !== false && column.field !== '__check__').map((column) => column.field)

  return (
    <MUIPaper
      data-testid="ThirdPartyFormsTableModule"
      sx={{
        position: 'relative',
        height: tableHeight ? `calc(${tableHeight}px - 20px)` : 'auto',
        width: '100%',
        boxShadow: 'none',
        backgroundColor: 'background.paper',
        mt: 3
      }}
    >
      {filtersReady && (
        <>
          <TableClearFilters
            filterCount={thirdPartyFormsTableFilterCount}
            onClear={handleClearAll}
            sx={{ position: 'absolute', top: '3px', right: '130px', zIndex: 2, mr: '3.25rem', mt: '0.35rem' }}
          />
          <DataGridPro
            apiRef={apiRef}
            data-testid="ThirdPartyFormsTable"
            rows={matterDocumentsTableData?.rows ?? []}
            getRowId={(row) => row.matterDocumentId}
            columns={orderedColumns}
            columnVisibilityModel={columnVisibilityModel}
            loading={matterDocumentsTableLoading}
            rowCount={matterDocumentsTableData?.totalRowCount ?? 0}
            paginationMode="server"
            sortingMode="server"
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={(newModel) => {
              setThirdPartyFormsTableFilters({
                pageSize: newModel.pageSize,
                startAtIndex: newModel.page * newModel.pageSize
              })
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            onColumnOrderChange={onColumnOrderChange}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            onColumnWidthChange={onColumnWidthChange}
            pinnedColumns={pinnedColumns}
            onPinnedColumnsChange={onPinnedColumnsChange}
            disableRowSelectionOnClick
            disableVirtualization
            checkboxSelection={false}
            getRowHeight={() => 64}
            headerFilters
            slotProps={{
              columnsManagement: { getTogglableColumns },
              loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
              toolbar: { onRefresh: handleRefresh, onColumnsClick: handleColumnsClick } as any
            }}
            slots={{
              columnMenu: (props: GridColumnMenuProps) => (
                <GridColumnMenu
                  {...props}
                  // @ts-ignore
                  sx={{ '.MuiTypography-body1': { mb: 0 } }}
                  slots={{ columnMenuFilterItem: null }}
                />
              ),
              headerFilterMenu: null,
              toolbar: CustomToolbar as any
            }}
            sx={{
              border: 'none',
              backgroundColor: 'background.paper',
              [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
              [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                outline: 'none'
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center'
              },
              '& .MuiDataGrid-cell .MuiTypography-noWrap': {
                minWidth: 0,
                flex: '1 1 auto'
              },
              '& .MuiTablePagination-selectLabel': {
                display: 'block !important'
              },
              '& .MuiTablePagination-input': {
                display: 'inline-flex !important',
                mb: 0,
                ml: 0,
                mt: '8px',
                fontSize: '0.875rem',
                '& .MuiTablePagination-select': {
                  padding: '0 !important',
                  ml: '4px',
                  mr: '20px',
                  minWidth: 'none',
                  fontSize: '12px',
                  '&:focus': { backgroundColor: 'transparent' }
                },
                '& .MuiSvgIcon-root': { width: '1.5rem', height: '1.5rem', top: 'calc(50% - 12px)' }
              },
              '& .MuiTablePagination-actions': { '& .MuiButtonBase-root': { padding: 0, top: '4px' } },
              [`& .MuiDataGrid-filler`]: { display: 'none' }
            }}
          />
        </>
      )}
    </MUIPaper>
  )
}
